// @flow
import * as React from 'react'
import groupBy from 'lodash/groupBy'
import DownChevronIcon from 'icon/DownChevronIcon'
import type { User } from 'types'
import { Button } from 'ui/Form'
import Link from 'ui/Link'
import { applySmallImagePreview } from 'util/uploadcare'
import { Layout, LayoutItem, type LinkManifest } from '../../index'
import { WhitelabelContext } from 'main/framework'
import { AssetName } from 'main/util/asset'

import './PageHeader.module.scss'

type Props = {
  user: User | null,
  linkManifests: LinkManifest[],
  showNavMenuPrompt: boolean,
  showNavMenu: boolean,
  onToggleNavMenu: () => void,
  children: React.Node,
}

export default class BasePageHeaderDesktop extends React.Component<Props> {
  navMenuNode: ?HTMLDivElement

  static contextType = WhitelabelContext

  componentDidMount = () => {
    window.document.addEventListener('mousedown', this.handleDocumentClick)
  }

  componentWillUnmount = () => {
    window.document.removeEventListener('mousedown', this.handleDocumentClick)
  }

  render = () => {
    const bgColor = this.context.getAsset(AssetName.LOGO_AND_SEARCH_BACKGROUND_COLOR)
    return (
      <div styleName="root">
        <Layout>
          <LayoutItem grow>
            <div styleName="children" css={{ backgroundColor: bgColor || 'inherit' }}>
              {this.props.children}
            </div>
          </LayoutItem>
          <LayoutItem>{this.renderNavMenuPromptMaybe(bgColor)}</LayoutItem>
        </Layout>
      </div>
    )
  }

  renderNavMenuPromptMaybe = (bgColor?: string) => {
    if (!this.props.showNavMenuPrompt || !this.props.user) return

    const modifier = this.props.showNavMenu ? 'showNavMenu' : ''
    const avatarUrl = applySmallImagePreview(this.props.user.avatarUrl)

    return (
      <div
        styleName={`navMenuPromptContainer ${modifier}`}
        css={{ backgroundColor: bgColor || 'inherit' }}
      >
        <Button appearance="naked" onClick={this.props.onToggleNavMenu}>
          <div styleName="navMenuPrompt">
            <img styleName="navMenuPromptAvatar" src={avatarUrl} />
            <div styleName="navMenuPromptChevron">
              <DownChevronIcon />
            </div>
          </div>
        </Button>

        {this.props.showNavMenu ? this.renderNavMenu() : null}
      </div>
    )
  }

  renderNavMenu = () => {
    const { linkManifests } = this.props
    const sectionlessLinkManifests = linkManifests.filter(m => !m.section)
    const groupedLinkManifests = groupBy(linkManifests, 'section')

    return (
      <div styleName="navMenu" ref={node => (this.navMenuNode = node)}>
        <div styleName="navMenuLinks">
          {this.renderNavSectionMaybe(null, sectionlessLinkManifests)}
          {this.renderNavSectionMaybe('My Account', groupedLinkManifests['My Account'])}
          {this.renderNavSectionMaybe('Wonderschool', groupedLinkManifests.Wonderschool)}
        </div>
      </div>
    )
  }

  renderNavSectionMaybe = (title: string | null, manifests?: LinkManifest[] = []) => {
    if (manifests.length === 0) return

    return (
      <div styleName="navMenuSection">
        {title && <div styleName="navMenuSectionTitle">{title}</div>}
        {manifests.map(manifest => (
          <NavMenuLink key={manifest.text} {...manifest} />
        ))}
      </div>
    )
  }

  handleDocumentClick = (event: SyntheticMouseEvent<HTMLElement>) => {
    if (this.props.showNavMenu && this.navMenuNode && !this.navMenuNode.contains(event.target)) {
      this.props.onToggleNavMenu()
    }
  }
}

function NavMenuLink(props: LinkManifest) {
  return (
    <div styleName="navMenuLink">
      <Link blend href={props.href} native={props.native || false} onClick={props.onClick}>
        {props.text}
      </Link>
    </div>
  )
}
